import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import parse from "html-react-parser";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    divisionImg = "divisionImg",
    divisionHeading = "divisionHeading",
    divisionDescription = "divisionDescription",
    primaryButton = "primaryButton",
    secondaryButton = "secondaryButton",
    primaryButtonLinkLabel = "primaryButtonLinkLabel",
    secondaryButtonLinkLabel = "secondaryButtonLinkLabel",
    primaryBtnBackgroundColor = "primaryBtnBackgroundColor",
    primaryBtnTextColor = "primaryBtnTextColor",
    secondaryButtonBackgroundColor = "secondaryButtonBackgroundColor",
    secondaryButtonTextColor = "secondaryButtonTextColor",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.divisionImg]: string;
        [fields.divisionHeading]: string;
        [fields.divisionDescription]: string;
        [fields.primaryButtonLinkLabel]: LinkFieldValue;
        [fields.secondaryButtonLinkLabel]: LinkFieldValue;
        [fields.primaryButton]: string;
        [fields.secondaryButton]: string;
        [fields.primaryBtnBackgroundColor]: string;
        [fields.primaryBtnTextColor]: string;
        [fields.secondaryButtonBackgroundColor]: string;
        [fields.secondaryButtonTextColor]: string;
    };
}

export interface CENDivisionHeroBannerStyle {
    bannerImageGridItem?: GridItemProps;
    bannerContentGridItem?: GridItemProps;
    button?: LinkPresentationProps;
    bannerHeading?: InjectableCss;
    bannerSubHeading?: InjectableCss;
    whiteButton?: LinkPresentationProps;
}

export const cenDivisionHeroBannerStyle: CENDivisionHeroBannerStyle = {
    bannerImageGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            align-self: flex-start;
            [class^="LazyImageStyle-sc"] {
                border: 4px solid #e0dfe0;
                height: auto;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    css`
                        margin-right: 80px;
                    `,
                    css`
                        margin-right: 80px;
                    `,
                    css`
                        margin-right: 80px;
                    `,
                    css`
                        margin-right: 80px;
                    `,
                ])}
        `,
    },
    bannerContentGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            flex-direction: column;
            padding-right: 26px;
        `,
    },
    bannerHeading: {
        css: css`
            margin-bottom: 24px;
        `,
    },
    bannerSubHeading: {
        css: css`
            margin-bottom: 32px;
            margin-top: 0;
            color: ${getProp("theme.typography.h5.color")};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: ${TYPOGRAPHY.size.large};
                    `,
                ])}
        `,
    },
};

const styles = cenDivisionHeroBannerStyle;

type Props = OwnProps & WidgetProps;

const CENDivisionHeroBanner = ({ fields }: Props) => {
    const linkOne = useGetLink(fields.primaryButtonLinkLabel);
    const linkTwo = useGetLink(fields.secondaryButtonLinkLabel);

    const whiteButton = {
        css: css`
            border: none;
            background-color: ${fields.secondaryButtonBackgroundColor};
            border-radius: 24px;
            margin-left: 10px;
            padding: 12px 24px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-left: 0px;
                        margin-top: 10px;
                    `,
                    css`
                        margin-left: 0px;
                        margin-top: 10px;
                    `,
                    css`
                        margin-left: 0px;
                        margin-top: 10px;
                    `,
                    null,
                ])}
            span {
                font-weight: ${TYPOGRAPHY.weight.mediumbold};
                font-size: ${TYPOGRAPHY.size.large};
                line-height: 24px;
                color: ${fields.secondaryButtonTextColor} !important;
            }
        `,
    };

    const button = {
        css: css`
            border-radius: 24px;
            background-color: ${fields.primaryBtnBackgroundColor};
            padding: 12px 24px;
            span {
                font-weight: ${TYPOGRAPHY.weight.mediumbold};
                font-size: ${TYPOGRAPHY.size.large};
                line-height: 24px;
                color: ${fields.primaryBtnTextColor} !important;
            }
        `,
    };

    return (
        <GridContainer
            css={css`
                padding-top: 32px;
            `}
        >
            <GridItem {...styles.bannerImageGridItem}>
                <LazyImage src={fields.divisionImg} altText={fields.divisionHeading} />
            </GridItem>
            <GridItem {...styles.bannerContentGridItem}>
                <Typography variant="h1" {...styles.bannerHeading}>
                    {fields.divisionHeading}
                </Typography>
                <Typography variant="h5" {...styles.bannerSubHeading}>
                    {parse(transformContentHtml(fields.divisionDescription), parserOptions)}
                </Typography>
                <StyledWrapper>
                    {fields.primaryButton && (
                        <Link {...button} href={linkOne.url}>
                            {fields.primaryButton}
                        </Link>
                    )}
                    {fields.secondaryButton && (
                        <Link {...whiteButton} href={linkTwo.url}>
                            {fields.secondaryButton}
                        </Link>
                    )}
                </StyledWrapper>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: CENDivisionHeroBanner,
    definition: {
        group: "Basic",
        displayName: " CEN Divsion Hero Banner",
        fieldDefinitions: [
            {
                name: fields.divisionImg,
                editorTemplate: "ImagePickerField",
                fieldType: "General",
                defaultValue: "",
            },
            {
                name: fields.divisionHeading,
                editorTemplate: "MultilineTextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.divisionDescription,
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.primaryButton,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.primaryButtonLinkLabel,
                displayName: "Primary Button Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "General",
            },
            {
                name: fields.primaryBtnBackgroundColor,
                fieldType: "General",
                editorTemplate: "ColorPickerField",
                defaultValue: "#8F0446",
                displayName: "Primary Background Color",
            },
            {
                name: fields.primaryBtnTextColor,
                fieldType: "General",
                editorTemplate: "ColorPickerField",
                defaultValue: "white",
                displayName: "Primary Text Color",
            },
            {
                name: fields.secondaryButton,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.secondaryButtonBackgroundColor,
                fieldType: "General",
                editorTemplate: "ColorPickerField",
                defaultValue: "white",
                displayName: "Secondary Background Color",
            },
            {
                name: fields.secondaryButtonTextColor,
                fieldType: "General",
                editorTemplate: "ColorPickerField",
                defaultValue: "#8F0446",
                displayName: "Secondary Text Color",
            },
            {
                name: fields.secondaryButtonLinkLabel,
                displayName: "Secondary Button Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
